import { Box, useTheme } from '@mui/material';
import React from 'react';

import {
  appendKPISummaryUnit,
  type KPISummaryUnit,
} from '../../utils/kpiSummary';

export interface KPIProgressBarProps {
  /** KPI summary value (can be negative) */
  value: number;
  valueUnit?: KPISummaryUnit;
  /** Target in the same unit as the KPI summary, e.g. 10 */
  target: number;
  /** Color used for positive progress */
  themeColor: string;
  /** Color used for small negatives (|value| ≤ target). Defaults to MUI error.light */
  warningColor?: string;
  /** Color used for large negatives (|value| > target). Defaults to MUI error.main */
  errorColor?: string;
  /** Bar height in pixels. Defaults to 8 */
  height?: number;
}

/**
 * Bidirectional KPI progress bar.
 *
 * | Condition              | Zero position              | Bar direction          | Bar color    |
 * |------------------------|----------------------------|------------------------|--------------|
 * | value > 0              | Left edge (0%)             | Extends right          | themeColor   |
 * | value == 0             | Left edge (0%)             | No bar                 | N/A          |
 * | value < 0              | Shifts right proportionally | Extends left from zero | warningColor / errorColor |
 *
 * For all negative values, zero position = |value|/target / (|value|/target + 1).
 * This ensures the negative bar width is always proportional to the positive
 * target region (Req 5.6). Color switches from warningColor to errorColor
 * when |value| exceeds target.
 */
export const KPIProgressBar: React.FC<KPIProgressBarProps> = ({
  value,
  valueUnit = 'percentage',
  target,
  themeColor,
  warningColor: warningColorProp,
  errorColor: errorColorProp,
  height = 8,
}) => {
  const theme = useTheme();
  const warningColor = warningColorProp ?? theme.palette.error.light;
  const errorColor = errorColorProp ?? theme.palette.error.main;

  // Guard against non-positive targets
  const safeTarget = target > 0 ? target : 1;

  // Determine layout parameters
  const isPositive = value > 0;
  const isZero = value === 0;
  const absValue = Math.abs(value);
  const isSmallNegative = value < 0 && absValue <= safeTarget;
  const isLargeNegative = value < 0 && absValue > safeTarget;

  let zeroPositionPct: number; // percentage from left edge where zero sits
  let barLeftPct: number; // bar left edge as percentage
  let barWidthPct: number; // bar width as percentage
  let barColor: string;

  if (isZero) {
    // No bar rendered
    zeroPositionPct = 0;
    barLeftPct = 0;
    barWidthPct = 0;
    barColor = themeColor;
  } else if (isPositive) {
    // Zero at left edge, bar extends right toward target
    zeroPositionPct = 0;
    barLeftPct = 0;
    barWidthPct = Math.min(value / safeTarget, 1) * 100;
    barColor = themeColor;
  } else {
    // Negative: zero shifts right proportionally so that the negative
    // region width is proportional to the positive target region.
    //
    // Req 5.6: negativeRatio = |value| / target
    //          zeroPosition  = negativeRatio / (negativeRatio + 1)
    //
    // Example: value = -20, target = 10 → ratio = 2
    //          zero at 2/3 ≈ 66.7% from left
    //          negative bar fills left 66.7%, right 33.3% = target zone
    //          → negative region is 2× the target region width ✓
    //
    // For small negatives (e.g. -5, target 10) → ratio = 0.5
    //          zero at 0.5/1.5 ≈ 33.3% from left
    //          negative bar fills left 33.3%
    const negativeRatio = absValue / safeTarget;
    zeroPositionPct = (negativeRatio / (negativeRatio + 1)) * 100;
    barLeftPct = 0;
    barWidthPct = zeroPositionPct; // bar fills from left edge to zero marker
    barColor = isLargeNegative ? errorColor : warningColor;
  }

  // Show zero marker for negative values (where zero isn't at the left edge)
  const showZeroMarker = value < 0;

  const ariaLabel = `KPI progress: ${appendKPISummaryUnit(value, valueUnit)} of ${appendKPISummaryUnit(safeTarget, valueUnit)} target`;

  return (
    <Box
      role='progressbar'
      aria-label={ariaLabel}
      aria-valuenow={value}
      aria-valuemin={
        isLargeNegative ? -absValue : isSmallNegative ? -safeTarget : 0
      }
      aria-valuemax={safeTarget}
      sx={{ position: 'relative', width: '100%', height, borderRadius: 1 }}
    >
      {/* Track background */}
      <Box
        data-testid='kpi-progress-track'
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#e0e0e0',
          borderRadius: 1,
        }}
      />

      {/* Filled bar */}
      {barWidthPct > 0 && (
        <Box
          data-testid='kpi-progress-bar'
          sx={{
            position: 'absolute',
            top: 0,
            left: `${barLeftPct}%`,
            width: `${barWidthPct}%`,
            height: '100%',
            backgroundColor: barColor,
            borderRadius: 1,
            transition: 'width 0.3s ease, left 0.3s ease',
            ...(value < 0 && {
              backgroundImage: `repeating-linear-gradient(
                                90deg,
                                ${barColor} 0px,
                                ${barColor} 6px,
                                transparent 6px,
                                transparent 10px
                            )`,
              backgroundColor: 'transparent',
            }),
          }}
        />
      )}

      {/* Zero midpoint marker */}
      {showZeroMarker && (
        <Box
          data-testid='kpi-zero-marker'
          sx={{
            position: 'absolute',
            top: -2,
            left: `${zeroPositionPct}%`,
            transform: 'translateX(-50%)',
            width: 2,
            height: height + 4,
            backgroundColor: 'text.primary',
            borderRadius: 0.5,
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
};
